// This Composable allows to "teleport" a component
// into a DOM node that exists outside the DOM hierarchy of that component.
// Vue 3 already supports it natively https://vuejs.org/guide/built-ins/teleport.html

import Vue, { watch } from 'vue'

interface Options {
  props?: Record<string, any>
  emits?: Record<string, (...args: any) => void>
  slots?: Record<string, any>
}

export function useTeleport(component: any, options: Options = {}) {
  const VueComponent = Vue.extend(component)
  const instance = new VueComponent({ propsData: options.props })

  if (options.emits) {
    Object.entries(options.emits).forEach(([event, callback]) => {
      instance.$on(event, callback)
    })
  }

  instance.$mount()

  function remove(): void {
    instance.$destroy()
    instance.$el.remove()
  }

  function render(target?: HTMLElement | null): void {
    ;(target ?? document.body).appendChild(instance.$el)
  }

  function updateSlots(): void {
    if (!options.slots) return

    Object.entries(options.slots).forEach(([name, slot]) => {
      instance.$slots[name] = [slot]
    })

    instance.$forceUpdate()
  }

  watch(() => options.slots, updateSlots, { deep: true, immediate: true })

  return {
    instance,
    render,
    remove,
  }
}
